import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { CSATRating } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { Star } from 'lucide-react';
import React from 'react';

/** Exactly 5 labels for star 1–5 (e.g. ease or usefulness scale) */
export type StarTooltipLabels = [string, string, string, string, string];

type RatingSelectorProps = {
  rateType: CSATRating | null;
  onChangeRating: (rating: CSATRating | null) => void;
  /**
   * Visual variant for the rating selector.
   * - 'emoji': Face emojis (default, used in CSAT feedback)
   * - 'star': 5-star rating scale
   */
  variant?: 'emoji' | 'star';
  /** Re-selecting the active star decrements by one instead of clearing the scale. */
  stepDownOnReselect?: boolean;
  /**
   * Optional labels for each star (1–5). When provided, hovering a star shows this tooltip.
   * Used for UMUX-Lite ease/usefulness scales.
   */
  tooltipLabels?: StarTooltipLabels;
};

const ratingValues = [
  CSATRating.Value1,
  CSATRating.Value2,
  CSATRating.Value3,
  CSATRating.Value4,
  CSATRating.Value5,
];

const emojiOptions = ['😠', '😔', '😐', '🙂', '😍'];

export const RatingSelector: React.FC<RatingSelectorProps> = ({
  rateType,
  onChangeRating,
  variant = 'emoji',
  stepDownOnReselect = false,
  tooltipLabels,
}) => {
  if (variant === 'star') {
    return (
      <div className="flex flex-row justify-center gap-1">
        {ratingValues.map((rating, index) => {
          const ratingNumber = index + 1;
          const currentRatingNumber = rateType ? ratingValues.indexOf(rateType) + 1 : 0;
          const isFilled = ratingNumber <= currentRatingNumber;
          const isHoverable = rateType === null || ratingNumber !== currentRatingNumber;
          const tooltipLabel = tooltipLabels?.[index];
          const ariaLabel =
            tooltipLabel ?? `${ratingNumber} star${ratingNumber > 1 ? 's' : ''}`;
          const handleStarClick = () => {
            if (rating !== rateType) {
              onChangeRating(rating);
              return;
            }
            onChangeRating(stepDownOnReselect ? (ratingValues[index - 1] ?? null) : null);
          };

          const starButton = (
            <button
              key={rating}
              onClick={handleStarClick}
              aria-label={ariaLabel}
              aria-pressed={rateType === rating}
              className={cn(
                'transition-all duration-150 ease-out',
                'rounded p-1',
                isHoverable && 'hover:scale-110',
                'active:scale-125'
              )}
              type="button"
            >
              <Star
                size={28}
                className={cn(
                  'transition-colors duration-150',
                  isFilled
                    ? 'fill-sunlight-400 text-sunlight-400'
                    : 'fill-transparent text-border-strong hover:text-sunlight-400'
                )}
              />
            </button>
          );

          if (tooltipLabel) {
            return (
              <Tooltip key={rating}>
                <TooltipTrigger asChild>{starButton}</TooltipTrigger>
                <TooltipContent side="top">{tooltipLabel}</TooltipContent>
              </Tooltip>
            );
          }
          return starButton;
        })}
      </div>
    );
  }

  // Default: emoji variant
  return (
    <div className="flex flex-row justify-between px-4">
      {ratingValues.map((rating, index) => (
        <button
          type="button"
          key={rating}
          onClick={() => onChangeRating(rating === rateType ? null : rating)}
          aria-label={`Rating ${index + 1}`}
          aria-pressed={rateType === rating}
          className={cn(
            'transition-all duration-200 ease-in-out',
            'hover:scale-110 active:scale-125',
            'rounded-full p-2',
            'text-3xl',
            rateType === null && 'grayscale-0 hover:opacity-100',
            rateType !== null &&
              rateType !== rating &&
              'opacity-50 grayscale hover:opacity-100 hover:grayscale-0',
            rateType === rating && 'scale-125 grayscale-0'
          )}
        >
          {emojiOptions[index]}
        </button>
      ))}
    </div>
  );
};
